import classNames from 'classnames';
import type { BackTestInputsProps } from 'components/common-form/sidePanel/backTest/BackTestInputs.connect';
import { QuoteLabel } from 'components/common-form/sidePanel/backTest/QuoteLabel';
import { RangePicker } from 'components/common-form/sidePanel/backTest/RangePicker';
import {
  datePresets,
  datePresetsTranslations,
  INITIAL_QUOTE_LABEL,
  SECOND_QUOTE_LABEL,
  useRunTestCb,
} from 'components/common-form/sidePanel/backTest/shared';
import { DatePicker } from 'components/shared/DatePicker/DatePicker';
import type { AnalyticCategoryActions } from 'models';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { DateMode } from 'state/backTest/backTest.model';
import { nowISODateString } from 'utils/date';

const trackEventFromDateMode: Record<DateMode, AnalyticCategoryActions['Backtest']> = {
  range: 'Date range backtest',
  multi: 'Custom dates backtest',
};

export function BackTestInputs({
  canAddExtraQuote,
  setSecondQuoteId,
  addSecondQuoteId,
  removeSecondQuoteId,
  setMaturities,
  maturities,
  maturityMode,
  setMaturityMode,
  quoteLabels,
  setInitialQuoteLabel,
  setSecondQuoteLabel,
  quoteIds,
  hasRunBackTest,
  hasErrorForInitialQuoteId,
  hasErrorForSecondQuoteId,
  runTest,
}: BackTestInputsProps) {
  const { formatMessage } = useIntl();
  const setMaturityModeCb = useCallback(
    (newMaturityMode: DateMode) => {
      logAnalytics.trackEvent('Backtest', trackEventFromDateMode[newMaturityMode]);
      setMaturityMode(newMaturityMode);
    },
    [setMaturityMode],
  );
  const runTestCb = useRunTestCb(runTest);
  return (
    <>
      <div className="d-flex flex-wrap">
        <div
          className={classNames('row input-group input-group-sm mb-2', {
            'is-highlighted-danger': hasErrorForInitialQuoteId,
          })}
        >
          <div className="col-6 p-0">
            <div className="d-flex h-100">
              <div className="input-group-prepend input-group-text h-100" style={{ backgroundColor: 'transparent' }}>
                <QuoteLabel shortLabel={INITIAL_QUOTE_LABEL} color="primary" />
              </div>
              <input className="form-control " id="quoteId" defaultValue={quoteIds.initialQuoteId} readOnly></input>
            </div>
          </div>
          <div className="col-5 p-0">
            <div className="d-flex h-100">
              <input
                className="form-control "
                id="quoteLabel"
                placeholder={formatMessage({ id: 'backTest.customQuoteLabel.placeholder' })}
                disabled={hasRunBackTest}
                defaultValue={quoteLabels.initialQuoteLabel}
                onChange={e => setInitialQuoteLabel(e.target.value)}
              ></input>
            </div>
          </div>
          <div className="col-1  input-group-text input-group-prepend h-100">
            <button
              className="btn btn-icon text-primary "
              disabled={hasRunBackTest || !canAddExtraQuote}
              onClick={addSecondQuoteId}
            >
              <i className="icon  icon-sm ">add_circle</i>
            </button>
          </div>
        </div>
        {quoteIds.secondQuoteId !== undefined && (
          <div
            className={classNames('row input-group input-group-sm mb-2', {
              'is-highlighted-danger': hasErrorForSecondQuoteId,
            })}
          >
            <div className="col-6 p-0">
              <div className="d-flex h-100">
                <div className="input-group-text input-group-prepend h-100">
                  <QuoteLabel shortLabel={SECOND_QUOTE_LABEL} color="danger" />
                </div>
                <input
                  placeholder={formatMessage({ id: 'backTest.secondQuoteId.placeholder' })}
                  className="form-control"
                  id="quoteId"
                  disabled={hasRunBackTest}
                  value={quoteIds.secondQuoteId ?? undefined}
                  autoFocus
                  onChange={e => setSecondQuoteId(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      runTestCb(maturityMode);
                    }
                  }}
                />
              </div>
            </div>
            <div className="col-5 p-0">
              <input
                className="form-control  h-100"
                id="quoteLabel"
                disabled={hasRunBackTest}
                defaultValue={quoteLabels.secondQuoteLabel}
                placeholder={formatMessage({ id: 'backTest.customQuoteLabel.placeholder' })}
                onChange={e => setSecondQuoteLabel(e.target.value)}
              />
            </div>
            <div className="col-1 input-group-text input-group-prepend h-100">
              <button className="btn btn-icon text-primary" disabled={hasRunBackTest} onClick={removeSecondQuoteId}>
                <i className="icon icon-sm">cancel</i>
              </button>
            </div>
          </div>
        )}
      </div>
      <label className="form-label d-flex w-100 my-2 align-items-center justify-content-between">
        <FormattedMessage id="backTest.maturity" />
        <div>
          <button
            className={classNames('btn btn-link', { 'text-info': maturityMode === 'range' })}
            onClick={() => setMaturityModeCb('range')}
            disabled={hasRunBackTest}
          >
            <FormattedMessage id="backTest.maturity.range" />
          </button>
          <button
            className={classNames('btn btn-link', { 'text-info': maturityMode === 'multi' })}
            onClick={() => setMaturityModeCb('multi')}
            disabled={hasRunBackTest}
          >
            <FormattedMessage id="backTest.maturity.selectedDates" />
          </button>
        </div>
      </label>
      <div className="d-flex w-100 pb-2">
        {maturityMode === 'range' && (
          <RangePicker disabled={hasRunBackTest} maturities={maturities} setMaturities={setMaturities} />
        )}
        {maturityMode === 'multi' && (
          <DatePicker
            disabled={hasRunBackTest}
            datePresetTranslations={datePresetsTranslations}
            datePresets={datePresets}
            type="multi"
            allowWeekend
            values={maturities}
            id="backtest-multi-picker"
            maxDate={nowISODateString()}
            onChange={setMaturities}
          />
        )}
      </div>
    </>
  );
}
